import * as React from 'react';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { AppPlatform, ReleaseStepType, SettingType } from '@/types';
import {
    SettingOutlined,
    GoldOutlined,
    CompassOutlined,
    AppstoreOutlined,
    MobileOutlined,
    HeartOutlined,
    ThunderboltOutlined,
    NotificationOutlined,
    CalendarOutlined,
    ControlOutlined,
    CheckSquareOutlined,
    FileTextOutlined,
    RocketOutlined,
    TeamOutlined,
} from '@ant-design/icons';

export interface MenuItem {
    id: string;
    label: string;
    path: string;
    icon: () => JSX.Element;
    showOnlyOnPlatforms?: AppPlatform[];
    subroute?: (orgId: string, appId: string) => string;
    isRunwayAdminRouteOnly?: boolean;
    minimumRequiredMigratedWorkflowStepsToShow?: ReleaseStepType[];
    minimumRequiredMigratedWorkflowStepsToHide?: ReleaseStepType[];
}

export const settingsMenuItems: MenuItem[] = [
    {
        id: 'general',
        label: 'General',
        path: `/${SettingType.General}`,
        icon: () => <SettingOutlined />,
    },
    {
        id: 'workflowSteps',
        label: 'Workflow steps',
        path: `/${SettingType.WorkflowSteps}`,
        icon: () => <GoldOutlined />,
        minimumRequiredMigratedWorkflowStepsToShow: [ReleaseStepType.BetaTesting],
    },
    {
        id: 'team',
        label: 'Team',
        path: `/${SettingType.Team}`,
        icon: () => <TeamOutlined />,
    },
    {
        id: 'releasePilotRotation',
        label: 'Release pilot rotation',
        path: `/${SettingType.ReleasePilotRotation}`,
        icon: () => <CompassOutlined />,
    },
    {
        id: 'integrations',
        label: 'Integrations',
        path: `/${SettingType.Integrations}`,
        icon: () => <AppstoreOutlined />,
    },
    {
        id: 'profilesAndDevices',
        label: 'Profiles and devices',
        path: `/${SettingType.ProfilesAndDevices}`,
        icon: () => <MobileOutlined />,
        showOnlyOnPlatforms: [AppPlatform.Ios],
    },
    {
        id: 'signingKeys',
        label: 'Signing keys',
        path: `/${SettingType.SigningKeys}`,
        icon: () => <MobileOutlined />,
        showOnlyOnPlatforms: [AppPlatform.Android],
    },
    {
        id: 'healthMetrics',
        label: 'Health metrics',
        path: `/${SettingType.HealthMetrics}`,
        icon: () => <HeartOutlined />,
    },
    {
        id: 'automations',
        label: 'Automations',
        path: `/${SettingType.Automations}`,
        icon: () => <ThunderboltOutlined />,
    },
    {
        id: 'notifications',
        label: 'Notifications',
        path: `/${SettingType.Notifications}`,
        icon: () => <NotificationOutlined />,
    },
    {
        id: 'schedule',
        label: 'Schedule',
        path: `/${SettingType.Schedule}`,
        icon: () => <CalendarOutlined />,
        subroute: (orgId, appId) => createAppSettingsRouteWithSettingType(orgId, appId, SettingType.ScheduleCadence),
    },
    {
        id: 'app-store',
        label: 'App store defaults',
        path: `/${SettingType.AppStore}`,
        icon: () => <ControlOutlined />,
        showOnlyOnPlatforms: [
            AppPlatform.Android,
            AppPlatform.Ios,
            AppPlatform.VisionOS,
            AppPlatform.TvOS,
            AppPlatform.WearOS,
        ],
        subroute: (orgId, appId) =>
            createAppSettingsRouteWithSettingType(orgId, appId, SettingType.DefaultReleaseNotes),
        minimumRequiredMigratedWorkflowStepsToHide: [ReleaseStepType.Metadata],
    },
    {
        id: 'checklists',
        label: 'Checklists',
        path: `/${SettingType.Checklists}`,
        icon: () => <CheckSquareOutlined />,
    },
    {
        id: 'custom-strings',
        label: 'Custom strings',
        path: `/${SettingType.CustomStrings}`,
        icon: () => <FileTextOutlined />,
    },
    {
        id: 'setup-tasks',
        label: 'Setup tasks',
        path: `/${SettingType.SetupTasks}`,
        icon: () => <RocketOutlined />,
    },
];
